// WP-1.5 (M9 TR-1..4): transfer queries/mutations over the engine-backed API.
import {
  transferListResponseSchema,
  transferResponseSchema,
  type TransferCreateRequest,
  type TransferResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { ACCOUNTS_KEY } from './accounts';

export const TRANSFERS_KEY = ['transfers'] as const;

export function useTransfers(
  filters: { status?: string; accountId?: string } = {},
): UseQueryResult<TransferResponse[]> {
  const params = new URLSearchParams();
  if (filters.status !== undefined) params.set('status', filters.status);
  if (filters.accountId !== undefined) params.set('accountId', filters.accountId);
  const qs = params.toString();
  return useQuery({
    queryKey: [...TRANSFERS_KEY, filters],
    queryFn: async () => {
      const body = transferListResponseSchema.parse(
        await api.get(`/api/v1/transfers${qs === '' ? '' : `?${qs}`}`),
      );
      return body.transfers;
    },
  });
}

function useInvalidateTransfers(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: TRANSFERS_KEY }),
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }), // balances changed too
    ]);
  };
}

export function useRecordTransfer(): UseMutationResult<
  TransferResponse,
  Error,
  TransferCreateRequest
> {
  const invalidate = useInvalidateTransfers();
  return useMutation({
    mutationFn: async (input: TransferCreateRequest) =>
      transferResponseSchema.parse(await api.post('/api/v1/transfers', input)),
    onSuccess: invalidate,
  });
}

export function useSettleTransfer(id: string): UseMutationResult<TransferResponse, Error, number> {
  const invalidate = useInvalidateTransfers();
  return useMutation({
    mutationFn: async (receivedMinor: number) =>
      transferResponseSchema.parse(
        await api.post(`/api/v1/transfers/${id}/settle`, { receivedMinor }),
      ),
    onSuccess: invalidate,
  });
}
